// components/casino/CasinoBetHistory.jsx
import React, { useState, useEffect } from "react";
import { useGetCasinoBetsQuery } from "../../store/apis/public-api";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/auth-slice";
import { format } from "date-fns";
import { STATUS_STYLES } from "../../constants/styles";
import { formatAmount } from "../../utils";

const CasinoBetHistory = () => {
  const user = useSelector(selectUser);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [gameFilter, setGameFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedBets, setExpandedBets] = useState({});
  const itemsPerPage = 10;

  const { data, isLoading, isError } = useGetCasinoBetsQuery({
    customer_id: user.id,
    page: currentPage,
    limit: itemsPerPage,
    status: statusFilter !== "all" ? statusFilter : undefined,
    game_alias: gameFilter !== "all" ? gameFilter : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const totalPages = data ? Math.ceil(data.count / itemsPerPage) : 1;

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const toggleExpand = (betId) => {
    setExpandedBets((prev) => ({
      ...prev,
      [betId]: !prev[betId],
    }));
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), "dd/MM/yyyy HH:mm");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getGameName = (alias) => {
    // You can replace this with a proper game name mapping
    const gameNames = {
      blackjack: "Blackjack",
      roulette: "Roulette",
      slots: "Slots",
      poker: "Poker",
      baccarat: "Baccarat",
    };
    return gameNames[alias] || alias;
  };

  return (
    <div className="p-4 md:p-6 bg-teal min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-white text-2xl md:text-3xl font-bold mb-6">
          Casino Bet History
        </h1>

        {/* Filters Section */}
        <div className="bg-forestGreen/80 rounded-t-xl shadow-dashboardshadow p-4 md:p-5">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex items-center gap-3">
                <label className="text-white text-sm font-medium">From:</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-teal border border-doubleextralightwhite/30 rounded-md px-3 py-2 text-white focus:outline-none focus:border-yellow"
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-white text-sm font-medium">To:</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-teal border border-doubleextralightwhite/30 rounded-md px-3 py-2 text-white focus:outline-none focus:border-yellow"
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex items-center gap-3">
                <label className="text-white text-sm font-medium">
                  Status:
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-teal border border-doubleextralightwhite/30 rounded-md px-3 py-2 text-white focus:outline-none focus:border-yellow"
                >
                  <option value="all">All</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-white text-sm font-medium">Game:</label>
                <select
                  value={gameFilter}
                  onChange={(e) => {
                    setGameFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-teal border border-doubleextralightwhite/30 rounded-md px-3 py-2 text-white focus:outline-none focus:border-yellow"
                >
                  <option value="all">All Games</option>
                  <option value="blackjack">Blackjack</option>
                  <option value="roulette">Roulette</option>
                  <option value="slots">Slots</option>
                  <option value="poker">Poker</option>
                  <option value="baccarat">Baccarat</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="bg-forestGreen shadow-dashboardshadow rounded-b-xl overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-white border-t-yellow"></div>
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center p-10 text-center">
              <div className="mb-4 w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
                <span className="text-red-500 text-2xl">!</span>
              </div>
              <h3 className="text-white font-medium mb-2">
                Error Loading Bets
              </h3>
              <p className="text-doubleextralightwhite text-sm max-w-md">
                There was an error loading your bet history. Please try again
                later.
              </p>
            </div>
          ) : data?.bets?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="text-left border-b border-doubleextralightwhite/20">
                    <th className="p-4 text-xs md:text-sm font-medium text-doubleextralightwhite uppercase">
                      Bet ID
                    </th>
                    <th className="p-4 text-xs md:text-sm font-medium text-doubleextralightwhite uppercase">
                      Date/Time
                    </th>
                    <th className="p-4 text-xs md:text-sm font-medium text-doubleextralightwhite uppercase">
                      Game
                    </th>
                    <th className="p-4 text-xs md:text-sm font-medium text-doubleextralightwhite uppercase">
                      Bet Amount
                    </th>
                    <th className="p-4 text-xs md:text-sm font-medium text-doubleextralightwhite uppercase">
                      Payout
                    </th>
                    <th className="p-4 text-xs md:text-sm font-medium text-doubleextralightwhite uppercase">
                      Status
                    </th>
                    <th className="p-4 text-xs md:text-sm font-medium text-doubleextralightwhite uppercase">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.bets.map((bet) => (
                    <React.Fragment key={bet.id}>
                      <tr className="hover:bg-forestGreen/60 bg-forestGreen/30">
                        <td className="p-4 text-white text-sm">
                          {bet.bet_id.substring(0, 8)}...
                        </td>
                        <td className="p-4 text-white text-sm">
                          {formatDate(bet.createdAt)}
                        </td>
                        <td className="p-4 text-white text-sm">
                          {getGameName(bet.game_alias)}
                        </td>
                        <td className="p-4 text-white text-sm">
                          {formatAmount(bet.bet_amount, "ETB", "en-ET")}
                        </td>
                        <td className="p-4 text-white text-sm">
                          {bet.won_amount
                            ? formatAmount(bet.won_amount, "ETB", "en-ET")
                            : "-"}
                        </td>
                        <td className="p-4">
                          <span
                            className={`px-3 py-1 rounded-md text-xs font-medium border ${
                              STATUS_STYLES[bet.status]?.bg
                            } ${STATUS_STYLES[bet.status]?.text} ${
                              STATUS_STYLES[bet.status]?.border
                            }`}
                          >
                            {bet.status.charAt(0).toUpperCase() +
                              bet.status.slice(1)}
                          </span>
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => toggleExpand(bet.id)}
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow/20 hover:bg-yellow/30 transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-4 w-4 transition-transform ${
                                expandedBets[bet.id] ? "rotate-90" : ""
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                        </td>
                      </tr>
                      {expandedBets[bet.id] && (
                        <tr>
                          <td colSpan="7" className="p-0">
                            <div className="p-4 border-t border-doubleextralightwhite/20 bg-teal/20">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <h3 className="font-medium text-yellow text-sm mb-2">
                                    Bet Details
                                  </h3>
                                  <div className="space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-doubleextralightwhite text-sm">
                                        Provider:
                                      </span>
                                      <span className="text-white text-sm">
                                        {bet.provider || "N/A"}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-doubleextralightwhite text-sm">
                                        Device:
                                      </span>
                                      <span className="text-white text-sm">
                                        {bet.device || "N/A"}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-doubleextralightwhite text-sm">
                                        Transaction ID:
                                      </span>
                                      <span className="text-white text-sm">
                                        {bet.trx_id || "N/A"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h3 className="font-medium text-yellow text-sm mb-2">
                                    Game Details
                                  </h3>
                                  <div className="space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-doubleextralightwhite text-sm">
                                        Round ID:
                                      </span>
                                      <span className="text-white text-sm">
                                        {bet.round_id || "N/A"}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-doubleextralightwhite text-sm">
                                        Session ID:
                                      </span>
                                      <span className="text-white text-sm">
                                        {bet.session_id || "N/A"}
                                      </span>
                                    </div>
                                    {bet.cashout_point && (
                                      <div className="flex justify-between">
                                        <span className="text-doubleextralightwhite text-sm">
                                          Cashout Point:
                                        </span>
                                        <span className="text-white text-sm">
                                          {bet.cashout_point}x
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-10 text-center">
              <div className="mb-4 w-16 h-16 bg-yellow/20 rounded-full flex items-center justify-center">
                <span className="text-yellow text-2xl">!</span>
              </div>
              <h3 className="text-white font-medium mb-2">No Bets Found</h3>
              <p className="text-doubleextralightwhite text-sm max-w-md">
                No casino bet history found for the selected filters. Try
                changing your filters or place some bets!
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {data?.bets?.length > 0 && (
          <div className="mt-6 flex justify-center">
            <div className="bg-forestGreen/80 rounded-lg shadow-dashboardshadow p-2 inline-flex gap-1 justify-center items-center">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded hover:bg-teal disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 brightness-200 rotate-180"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-md text-sm ${
                      currentPage === pageNum
                        ? "bg-yellow text-black"
                        : "text-white hover:bg-teal/50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {totalPages > 5 && currentPage < totalPages - 2 && (
                <span className="text-doubleextralightwhite px-1">...</span>
              )}

              {totalPages > 5 && currentPage < totalPages - 2 && (
                <button
                  onClick={() => handlePageChange(totalPages)}
                  className={`w-8 h-8 flex items-center justify-center rounded-md text-sm ${
                    currentPage === totalPages
                      ? "bg-yellow text-black"
                      : "text-white hover:bg-teal/50"
                  }`}
                >
                  {totalPages}
                </button>
              )}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded hover:bg-teal disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 brightness-200"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CasinoBetHistory;
